import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAlpaca } from '../../context/AlpacaContext';
import { useDemo } from '../../context/DemoContext';
import { useTour } from '../../context/TourContext';
import { alpacaService } from '../../services/AlpacaService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './Onboarding.module.css';

type Step = 'welcome' | 'connect' | 'connected' | 'personality' | 'risk' | 'notifications' | 'ready';

const STEPS: Step[] = ['welcome', 'connect', 'connected', 'personality', 'risk', 'notifications', 'ready'];

const PERSONALITIES = [
  {
    id: 'balanced',
    icon: '⚖️',
    name: 'Balanced',
    desc: 'Mix of long/short, consistent risk-adjusted returns. Hold winners, cut losers.',
    risk: 'Medium',
  },
  {
    id: 'conservative',
    icon: '🛡️',
    name: 'Conservative Income',
    desc: 'Capital preservation first. Only extreme setups, tight stops, early take profits.',
    risk: 'Low',
  },
  {
    id: 'momentum',
    icon: '🔥',
    name: 'Momentum Scalper',
    desc: 'Aggressive breakouts with volume. Ride winners hard, bigger positions.',
    risk: 'High',
  },
  {
    id: 'contrarian',
    icon: '🔄',
    name: 'Contrarian Value',
    desc: 'Buy fear, sell greed. Deep oversold/overbought. Patient multi-day swings.',
    risk: 'Medium-High',
  },
];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isConnected, alpacaAccount, connectPaper, connectLive, isLoading } = useAlpaca();
  const { enterDemo } = useDemo();
  const { hasSeenTour, startTour } = useTour();

  // Allow OAuthCallback to redirect here with step=connected
  const initialStep: Step = (location.state as any)?.step === 'connected' && isConnected ? 'connected' : 'welcome';

  const [currentStep, setCurrentStep] = useState<Step>(initialStep);

  // Config selections
  const [personality, setPersonality] = useState('balanced');
  const [maxPositions, setMaxPositions] = useState(5);
  const [dailyTarget, setDailyTarget] = useState(0);
  const [maxBpPct, setMaxBpPct] = useState(20);
  const [saving, setSaving] = useState(false);
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [notifEmail, setNotifEmail] = useState('');

  // If Alpaca becomes connected (e.g. after OAuth redirect), jump to success
  useEffect(() => {
    if (isConnected && currentStep === 'connect') {
      setCurrentStep('connected');
    }
  }, [isConnected, currentStep]);

  const stepIndex = STEPS.indexOf(currentStep);

  const handleLaunchDashboard = async () => {
    setSaving(true);
    try {
      await alpacaService.saveStrategistConfig({
        enabled: true,
        personality: personality as any,
        maxConcurrentPositions: maxPositions,
        dailyPnlTargetPct: dailyTarget,
        maxBuyingPowerPct: maxBpPct,
        autonomyLevel: 'full_auto',
      });
      // Save notification preferences if email provided
      if (emailNotifs && notifEmail.trim()) {
        const token = await import('../../services/authUtils').then((m) => m.getAuthToken());
        if (token) {
          const { API_URL } = await import('../../config');
          await fetch(`${API_URL}/api/settings/notifications`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              emailEnabled: true,
              email: notifEmail.trim(),
              inAppEnabled: true,
              alertTypes: {
                positionThreshold: true,
                botSignal: true,
                stopTriggered: true,
                strategist: true,
                morningBrief: true,
              },
            }),
          });
        }
      }
    } catch (err) {
      console.warn('Config save failed, continuing anyway:', err);
    }
    setSaving(false);
    if (!hasSeenTour) startTour();
    navigate('/home');
  };

  const handleTryDemo = () => {
    enterDemo();
    if (!hasSeenTour) startTour();
    navigate('/home');
  };

  const formatCurrency = (value: string | undefined) => {
    if (!value) return '$0.00';
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const renderStepper = () => (
    <div className={styles.stepper}>
      {STEPS.map((step, i) => (
        <React.Fragment key={step}>
          {i > 0 && <div className={i <= stepIndex ? styles.stepLineCompleted : styles.stepLine} />}
          <div
            className={
              i === stepIndex ? styles.stepDotActive : i < stepIndex ? styles.stepDotCompleted : styles.stepDot
            }
          />
        </React.Fragment>
      ))}
    </div>
  );

  const renderWelcome = () => (
    <div className={styles.stepContent} key="welcome">
      <SignalLoader message="" size="small" />
      <h1 className={styles.welcomeTitle}>Welcome to SignalDeck!</h1>
      <p className={styles.welcomeSubtitle}>
        Your AI-powered trading dashboard. Connect your brokerage account to get real-time positions, analytics, and
        smart trading signals.
      </p>
      <button className={styles.btnNext} onClick={() => setCurrentStep('connect')}>
        Get Started
      </button>
      <button className={styles.skipLink} onClick={handleTryDemo}>
        Try Demo First
      </button>
    </div>
  );

  const renderConnect = () => (
    <div className={styles.stepContent} key="connect">
      <h2 className={styles.connectTitle}>Connect Your Brokerage</h2>
      <p className={styles.connectDescription}>
        Link your Alpaca account to enable live trading data, positions tracking, and order execution right from your
        dashboard.
      </p>

      <div className={styles.alpacaInfo}>
        <p className={styles.alpacaInfoTitle}>What is Alpaca?</p>
        <p className={styles.alpacaInfoText}>
          Alpaca is a commission-free stock and options trading API. You can create a free paper trading account to
          practice with virtual money, or connect a live account for real trading.
        </p>
      </div>

      <div className={styles.disclosure}>
        <p className={styles.disclosureText}>
          Authorize <strong>SignalDeck</strong>. By allowing SignalDeck to access your Alpaca account, you are granting
          SignalDeck access to your account information and authorization to place transactions in your account at your
          direction. Alpaca does not warrant or guarantee that SignalDeck will work as advertised or expected. Before
          authorizing,{' '}
          <a href="/" className={styles.disclosureLink}>
            learn more about SignalDeck
          </a>
          .
        </p>
      </div>

      <div className={styles.connectButtons}>
        <button className={styles.btnPrimary} onClick={connectPaper}>
          Connect Paper Account
        </button>
        <button className={styles.btnSecondary} onClick={connectLive}>
          Connect Live Account
        </button>
        <span className={styles.liveNote}>Live trading requires an approved Alpaca account</span>
      </div>
    </div>
  );

  const renderConnected = () => (
    <div className={styles.stepContent} key="connected">
      <div className={styles.successIcon}>
        <span className={styles.successIconCheck}>&#10003;</span>
      </div>
      <h2 className={styles.successTitle}>Account Connected!</h2>
      <p className={styles.successSubtitle}>Your Alpaca account is linked and ready to go.</p>

      {alpacaAccount && (
        <div className={styles.accountCard}>
          <div className={styles.accountStat}>
            <span className={styles.accountStatLabel}>Equity</span>
            <span className={styles.accountStatValue}>{formatCurrency(alpacaAccount.equity)}</span>
          </div>
          <div className={styles.accountStat}>
            <span className={styles.accountStatLabel}>Buying Power</span>
            <span className={styles.accountStatValue}>{formatCurrency(alpacaAccount.buying_power)}</span>
          </div>
        </div>
      )}

      <button className={styles.btnNext} onClick={() => setCurrentStep('personality')}>
        Next: Choose Your Style
      </button>
    </div>
  );

  const renderPersonality = () => (
    <div className={styles.stepContent} key="personality">
      <h2 className={styles.connectTitle}>Choose Your Trading Style</h2>
      <p className={styles.connectDescription}>
        This sets how the AI Strategist thinks about trades. You can change it anytime from the Strategist page.
      </p>

      <div className={styles.personalityGrid}>
        {PERSONALITIES.map((p) => (
          <button
            key={p.id}
            className={`${styles.personalityCard} ${personality === p.id ? styles.personalityCardSelected : ''}`}
            onClick={() => setPersonality(p.id)}
          >
            <span className={styles.personalityIcon}>{p.icon}</span>
            <span className={styles.personalityName}>{p.name}</span>
            <span className={styles.personalityDesc}>{p.desc}</span>
            <span className={styles.personalityRisk}>Risk: {p.risk}</span>
          </button>
        ))}
      </div>

      <div className={styles.navButtons}>
        <button className={styles.backBtn} onClick={() => setCurrentStep('connected')}>
          Back
        </button>
        <button className={styles.btnNext} onClick={() => setCurrentStep('risk')}>
          Next
        </button>
      </div>
    </div>
  );

  const renderRisk = () => (
    <div className={styles.stepContent} key="risk">
      <h2 className={styles.connectTitle}>Set Your Risk Parameters</h2>
      <p className={styles.connectDescription}>
        These guardrails protect your capital. The AI Strategist will respect these limits on every trade.
      </p>

      <div className={styles.riskGrid}>
        <div className={styles.riskField}>
          <label className={styles.riskLabel}>Max Positions</label>
          <select
            className={styles.riskSelect}
            value={maxPositions}
            onChange={(e) => setMaxPositions(Number(e.target.value))}
            title="Max concurrent positions"
          >
            {[3, 5, 7, 10].map((n) => (
              <option key={n} value={n}>
                {n} positions
              </option>
            ))}
          </select>
          <span className={styles.riskHint}>Maximum open positions at once</span>
        </div>

        <div className={styles.riskField}>
          <label className={styles.riskLabel}>Max per Trade</label>
          <select
            className={styles.riskSelect}
            value={maxBpPct}
            onChange={(e) => setMaxBpPct(Number(e.target.value))}
            title="Max buying power per trade"
          >
            {[10, 15, 20, 25, 30].map((n) => (
              <option key={n} value={n}>
                {n}% of buying power
              </option>
            ))}
          </select>
          <span className={styles.riskHint}>Buying power allocated per trade</span>
        </div>

        <div className={styles.riskField}>
          <label className={styles.riskLabel}>Daily P&L Target</label>
          <select
            className={styles.riskSelect}
            value={dailyTarget}
            onChange={(e) => setDailyTarget(Number(e.target.value))}
            title="Daily P&L target"
          >
            <option value={0}>Off</option>
            {[0.5, 1, 1.5, 2, 3].map((n) => (
              <option key={n} value={n}>
                +{n}% daily
              </option>
            ))}
          </select>
          <span className={styles.riskHint}>AI reduces risk once target is hit</span>
        </div>
      </div>

      <div className={styles.navButtons}>
        <button className={styles.backBtn} onClick={() => setCurrentStep('personality')}>
          Back
        </button>
        <button className={styles.btnNext} onClick={() => setCurrentStep('notifications')}>
          Next
        </button>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className={styles.stepContent} key="notifications">
      <h2 className={styles.connectTitle}>Stay in the Loop</h2>
      <p className={styles.connectDescription}>
        Get alerts when the AI Strategist makes trades, positions hit targets, or the morning brief is ready.
      </p>

      <div className={styles.notifOptions}>
        <label className={styles.notifToggle}>
          <input
            type="checkbox"
            checked={emailNotifs}
            onChange={(e) => setEmailNotifs(e.target.checked)}
            className={styles.notifCheckbox}
          />
          <span className={styles.notifToggleLabel}>Email Notifications</span>
        </label>

        {emailNotifs && (
          <div className={styles.notifEmailField}>
            <input
              type="email"
              value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)}
              placeholder="you@example.com"
              className={styles.notifEmailInput}
            />
            <span className={styles.riskHint}>Trade alerts, morning briefs, and stop-loss notifications</span>
          </div>
        )}

        <div className={styles.notifInfo}>
          <p className={styles.notifInfoText}>
            You can also enable SMS notifications and customize alert types later in Settings.
          </p>
        </div>
      </div>

      <div className={styles.navButtons}>
        <button className={styles.backBtn} onClick={() => setCurrentStep('risk')}>
          Back
        </button>
        <button className={styles.btnNext} onClick={() => setCurrentStep('ready')}>
          {emailNotifs && notifEmail.trim() ? 'Next' : 'Skip for Now'}
        </button>
      </div>
    </div>
  );

  const renderReady = () => {
    const selectedPersonality = PERSONALITIES.find((p) => p.id === personality) || PERSONALITIES[0];
    return (
      <div className={styles.stepContent} key="ready">
        <div className={styles.successIcon}>
          <span className={styles.successIconCheck}>🚀</span>
        </div>
        <h2 className={styles.successTitle}>You're All Set!</h2>
        <p className={styles.successSubtitle}>Your AI Strategist will start analyzing the market on your behalf.</p>

        <div className={styles.readySummary}>
          <div className={styles.readyRow}>
            <span className={styles.readyLabel}>Personality</span>
            <span className={styles.readyValue}>
              {selectedPersonality.icon} {selectedPersonality.name}
            </span>
          </div>
          <div className={styles.readyRow}>
            <span className={styles.readyLabel}>Max Positions</span>
            <span className={styles.readyValue}>{maxPositions}</span>
          </div>
          <div className={styles.readyRow}>
            <span className={styles.readyLabel}>Max per Trade</span>
            <span className={styles.readyValue}>{maxBpPct}%</span>
          </div>
          <div className={styles.readyRow}>
            <span className={styles.readyLabel}>Daily Target</span>
            <span className={styles.readyValue}>{dailyTarget > 0 ? `+${dailyTarget}%` : 'Off'}</span>
          </div>
          <div className={styles.readyRow}>
            <span className={styles.readyLabel}>Autonomy</span>
            <span className={styles.readyValue}>Full Auto</span>
          </div>
        </div>

        <div className={styles.navButtons}>
          <button className={styles.backBtn} onClick={() => setCurrentStep('notifications')}>
            Back
          </button>
          <button className={styles.btnNext} onClick={handleLaunchDashboard} disabled={saving}>
            {saving ? 'Saving...' : 'Launch Dashboard'}
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.onboarding}>
        <SignalLoader message="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.onboarding}>
      {renderStepper()}
      {currentStep === 'welcome' && renderWelcome()}
      {currentStep === 'connect' && renderConnect()}
      {currentStep === 'connected' && renderConnected()}
      {currentStep === 'personality' && renderPersonality()}
      {currentStep === 'risk' && renderRisk()}
      {currentStep === 'notifications' && renderNotifications()}
      {currentStep === 'ready' && renderReady()}
    </div>
  );
};

export default Onboarding;
